import React from "react";
import { BCMSRecord, BCMSDefaultModel, Workflow, useGlobalController } from "bcms-react";
import { MobileNickname } from "model";
import MobileNicknamesPage from "components/pages/MobileNicknamesPage";

// Steps of the workflow
const steps = [ <MobileNicknamesPage /> ];

/**
 * @function MobileNicknamesWorkflow
 * @param {Void} - No arguments are passed.
 * @returns {React.Component}
 * @exported true
* @description When a new "Account" is created in the model xml, 
* create a "mobileNickname" entry in the model xml as well.
* This is probably how the actual product works since the user 
* can't create a "Mobile Nickname" in the product,
* but they have access to mobile nickname entries they can edit.
* 
 * Setting constructor example for state captures:
 * 
 * document.dispatchEvent(new CustomEvent("force-workflow-state", {
  detail: { 
    value: { 
      mobileNicknames: [
        { id: "0", account: "1", nickname: "some nickname", enabled: false, primary: false },
        { id: "1", account: "0", nickname: "some nickname 2", enabled: true, primary: true }
      ]
    } 
  } 
}));
 */
export default function MobileNicknamesWorkflow(props) {
    const controller = useGlobalController();
    // Get the inital state of the workflow
    const value = getDefault(controller);
    // Define the constructor function for forcing state through the 'force-workflow-state' event
    const constructor = record => {
        const mobileNicknamesObj = record.mobileNicknames ?? [];

        const mobileNicknames = mobileNicknamesObj.reduce((acc, mobileNickname) => {
            let mobileNicknameInstance = MobileNickname.initByID(mobileNickname.id);
            mobileNicknameInstance = mobileNicknameInstance === undefined ? 
            BCMSDefaultModel.mobileNickname()
            : MobileNickname.initByID(mobileNickname.id);

            const mergedObj = { ...mobileNicknameInstance.record, ...mobileNickname };
            const mergedMobileNickname = MobileNickname.init(mergedObj);
            acc.push(mergedMobileNickname);
            return acc;
        }, []);

        const mobileNicknamesProp = mobileNicknames.length > 0 ? { mobileNicknames: mobileNicknames } : {};

        // Return the new workflow default object.
        return { 
            ...getDefault(controller),
            ...mobileNicknamesProp
        };
    };

    // Return the workflow component
    return <Workflow value={ value } steps={ steps } constructor={ constructor } />
}
// Return the steps of the workflow
export const getWorkflowSteps = () => steps;

/**
 * @function getDefault
 * @param {Controller} controller - A global controller.
 * @returns {Object}
 * @exported true
 * @description Function to get the default state of the workflow
 */
export const getDefault = controller => {
    BCMSRecord.globalControllerFor([ MobileNickname ], controller);
    // Initalize mobile devices and accounts for the user.
    const mobileNicknames = MobileNickname.initAll();
    
    // Return the default workflow state
    return { 
        mobileNicknames
    };
};